import { useStage1SubmissionDocData, useStatDocData } from '../../../api/game';
import { SingleStage, stages } from '../../../lib/Problems';
import { calcRankMessage } from '../../../lib/stageResults';
import { useCurrentUser } from '../../../redux/selectors/authSelectors';
import StageTitle from '../../../static/png/Stage_title.png';
import Loading from '../common/Loading';
import { SingleState, SingleStateAnswer } from './Game';
import { faChevronDown, faStopwatch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CTResultScore,
  CommonBG,
  Mission,
  Overlay,
  MainButton as _Button,
  OverlayContent as _OverlayContent,
} from '@riddler-co-jp/specc-ui-components';
import * as React from 'react';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

interface ResultFinalProps {
  onBack?: () => void;
}

const ResultFinal: React.FC<ResultFinalProps> = props => {
  const { eventId } = useParams<'eventId'>();
  const uid = useCurrentUser()?.uid;
  const [submission, loadSubmission, submissionError] =
    useStage1SubmissionDocData(eventId, uid);
  const [stat, loadStat, statError] = useStatDocData(eventId);

  const maxScore = stages.reduce((a: number, b: SingleStage[]) => {
    return a + b.length;
  }, 0);

  const [showOverlay, setShowOverlay] = React.useState(-1);
  const [showOverlayQuestion, setShowOverlayQuestion] = React.useState(-1);

  const navigate = useNavigate();
  const goTo = (url: string) => {
    navigate(url);
  };
  const params: SingleStateAnswer[] = useMemo(() => {
    if (submission == null) return [];
    if (stat == null) return [];
    return submission.answers.map((result: SingleState, i) => {
      return { ...result, rates: stat.rate[i].rate.map(e => e * 100) };
    });
  }, [submission, submission?.answers, loadSubmission, stat]);

  const rank = useMemo(
    () => calcRankMessage(submission?.totalScore ?? 0, 0),
    [submission?.totalScore]
  );
  const average = React.useMemo(() => {
    return stat?.averageScore ?? 0;
  }, [stat, loadStat]);

  if (submission == null || stat == null) {
    return <Loading />;
  }

  return (
    <CommonBG
      withHeader
      withFooter
      headerOptions={{ buttonType: 'logout', logoType: 'CT' }}
    >
      <Overlay
        isVisible={showOverlay >= 0}
        onClick={() => {
          setShowOverlay(-1);
        }}
      >
        {showOverlay >= 0 && (
          <OverlayContent>
            <img src={stages[showOverlay][showOverlayQuestion].image} />
            <p>{stages[showOverlay][showOverlayQuestion].comment}</p>
          </OverlayContent>
        )}
      </Overlay>
      <Rule>
        <Title src={StageTitle} />
        <Mission>多角的な思考で法則問題を解きなさい。</Mission>
        <Time>
          <FontAwesomeIcon icon={faStopwatch} />
          制限時間 8分
        </Time>
        <VideoWrapper>
          <video controls>
            <source
              src={
                'https://storage.googleapis.com/specc-ct-movie/1-1Switch_%E3%83%AB%E3%83%BC%E3%83%AB%E8%AA%AC%E6%98%8E.mp4'
              }
              type='video/mp4'
            />
            <p>Your browser doesn't support HTML5 video.</p>
          </video>
        </VideoWrapper>
        <CTResultScore
          score={submission.totalScore}
          maxScore={maxScore}
          rank={rank}
          average={average}
        />
        <Description>
          <h2>あなたの解答</h2>

          {stages.map((stage: SingleStage[], n: number) => {
            return (
              <SingleAnswer
                params={params}
                stage={stage}
                n={n}
                onClickThumb={(i: number) => {
                  setShowOverlay(n);
                  setShowOverlayQuestion(i);
                }}
              />
            );
          })}
        </Description>
        <Buttons>
          <Button
            color='negative'
            size='large'
            onClick={() => {
              const historyUrl =
                'https://specc-dev.riddler.co.jp/history/' + eventId;
              location.href = historyUrl;
            }}
          >
            戻る
          </Button>
        </Buttons>
      </Rule>
    </CommonBG>
  );
};

type SingleAnswerProps = {
  params: SingleStateAnswer[] | null;
  stage: SingleStage[];
  n: number;
  onClickThumb: (i: number) => void;
};

const SingleAnswer: React.FC<SingleAnswerProps> = (
  props: SingleAnswerProps
) => {
  const { params, stage, n, onClickThumb } = props;
  const cat = ['A', 'B', 'C', 'D'];
  const [open, setOpen] = React.useState(false);

  let correctCount = 0;
  if (params != null) {
    stage.forEach((question: SingleStage, i: number) => {
      if (params[n].answers[i] == question.answer) {
        correctCount++;
      }
    });
  }

  return (
    <div>
      <SetHeader
        onClick={() => {
          setOpen(!open);
        }}
      >
        <h3>セット{cat[n]}</h3>
        <p>
          スコア<b>{correctCount}</b>/{stage.length}
        </p>
        <FontAwesomeIcon
          icon={faChevronDown}
          style={{ transform: `rotate(${open ? '0deg' : '180deg'})` }}
        />
      </SetHeader>

      <ListWrapper style={{ maxHeight: !open ? '600px' : '0px' }}>
        <ListHeader>
          <p>問題</p>
          <p>解答</p>
          <p>正答率</p>
        </ListHeader>

        {stage.map((question: SingleStage, i: number) => {
          let rate = null;

          if (params != null) {
            rate = params[n].rates[i];
          }

          return (
            <SingleList key={`res-${i}`}>
              <Example>
                <StageNum>{question.stage}</StageNum>
                <div
                  onClick={() => {
                    onClickThumb(i);
                  }}
                >
                  <img src={question.image} />
                </div>
                {/*
                  <div>
                    <p>{question.answer}</p>
                  </div>
                */}
              </Example>

              <Answer>
                {params == null ? (
                  <>
                    <InCorrect />
                    <p style={{ opacity: 0.3 }}>(未解答)</p>
                  </>
                ) : (
                  <>
                    {params[n].answers[i] == question.answer ? (
                      <Correct />
                    ) : (
                      <InCorrect />
                    )}
                    {params[n].finished[i] ? (
                      <p style={{ fontSize: '3rem' }}>{params[n].answers[i]}</p>
                    ) : (
                      <p style={{ opacity: 0.3 }}>(未解答)</p>
                    )}
                  </>
                )}
              </Answer>
              <Rate>{rate != null && <>{rate}% </>}</Rate>
            </SingleList>
          );
        })}
      </ListWrapper>
    </div>
  );
};

const Rate = styled.div`
  font-size: 2rem;
  font-weight: bold;
  margin-right: 20px;
  width: 90px;
`;

const Correct = () => {
  return (
    <svg
      id='Component_-_Complete'
      data-name='Component - Complete'
      xmlns='http://www.w3.org/2000/svg'
      width='20'
      height='20'
      viewBox='0 0 20 20'
    >
      <path
        id='パス_206'
        data-name='パス 206'
        d='M10,0A10,10,0,1,1,0,10,10,10,0,0,1,10,0Z'
        fill='#05aa70'
      />
      <path
        id='Checkbox'
        d='M4.6,8,0,3.4l.933-.933L4.6,6.067,10.667,0,11.6.933Z'
        transform='translate(4.001 6)'
        fill='#fff'
      />
    </svg>
  );
};

const InCorrect = () => {
  return (
    <svg
      id='Component_-_Error'
      data-name='Component - Error'
      xmlns='http://www.w3.org/2000/svg'
      width='20'
      height='20'
      viewBox='0 0 20 20'
    >
      <circle
        id='楕円形_111'
        data-name='楕円形 111'
        cx='10'
        cy='10'
        r='10'
        fill='#ea0e49'
      />
      <path
        id='Icon_-_Close'
        data-name='Icon - Close'
        d='M5,5.909.909,10,0,9.091,4.091,5,0,.909.909,0,5,4.091,9.091,0,10,.909,5.909,5,10,9.091,9.091,10Z'
        transform='translate(5 5)'
        fill='#fff'
      />
    </svg>
  );
};

const SetHeader = styled.div`
  border: 1px solid #ced4da;
  display: flex;
  background: white;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  margin-bottom: 10px;
  margin-top: 10px;
  align-items: center;
  cursor: pointer;

  h3 {
    background: #189bf2;
    color: white;
    font-weight: bold;
    font-size: 2.8rem;
    padding: 4px 18px;
    padding-bottom: 8px;
  }

  p {
    display: block;
    margin-left: auto;
    font-weight: bold;
    font-size: 1.8rem;
  }

  b {
    font-size: 2.8rem;
    border: none !important;
    margin: 4px;
  }

  svg {
    font-size: 2.6rem;
    margin: 0 16px;
    transition: 0.2s;
  }
`;

const OverlayContent = styled(_OverlayContent)`
  img {
    width: 800px !important;
  }
  p {
    width: 100% !important;
  }
`;

const StageNum = styled.div`
  margin-top: 10px;
  margin-left: 10px;
  background: #343a40;
  color: white;
  font-size: 2rem;
  font-weight: bold;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ListWrapper = styled.div`
  border: 1px solid #ced4da;
  overflow: hidden;
  transition: 0.2s;
`;

const ListHeader = styled.div`
  display: flex;
  background: #f3f4f6;
  font-weight: bold;
  font-size: 1.8rem;
  padding: 12px;

  >p: nth-child(1) {
    flex: 1;
    padding-left: 50px;
  }
  >p: nth-child(2) {
    flex: 0.9;
  }
  >p: nth-child(3) {
    flex: 0.4;
  }
`;

const SingleList = styled.div`
  display: flex;
  position: relative;
  align-items: center;

  &:nth-child(2n) {
    background: #ffffff;
  }

  &:nth-child(2n-1) {
    background: #f3f4f6;
  }
`;

const Example = styled.div`
  flex: 1;
  display: flex;

  img {
    cursor: pointer;
    width: 200px;
    height: auto;
    margin: 10px;
    border: 1px solid #ced4da;
  }

  > div:last-child {
    font-weight: bold;
    font-size: 1.8rem;
    display: flex;
    align-items: center;

    p {
      margin: 10px;
    }
  }
`;

const ResultHeader = styled.div`
  border-bottom: 4px solid #189bf2;
  margin: 0 3rem;
  display: flex;
  justify-content: center;
  align-items: flex-end;

  img {
    margin: 2.4rem 0;
    width: 212px;
    height: 32px;
  }
`;
const Answer = styled.div`
  flex: 0.8;

  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-weight: bold;

  svg {
    margin-right: 20px;
  }
`;

const Time = styled.div`
  background: rgba(206, 212, 218, 0.25);
  font-size: 1.8rem;
  font-weight: bold;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 4px 12px;
  margin-top: -1rem;

  svg {
    margin-right: 4px;
  }
`;

const VideoWrapper = styled.div`
  width: 100%;
  margin: 1rem auto;
  border: solid 2px #6c7680;

  video {
    width: 100%;
    margin-bottom: -4px;
  }
`;

const Title = styled.img`
  display: block;
  height: 80px;
  width: auto;
  margin: 4rem auto 0;
`;

const Button = styled(_Button).attrs({
  variant: 'main',
})`
  max-width: 47%;
  margin: 0 1rem;

  &:last-child {
    margin-right: 0;
  }
`;

const Buttons = styled.div`
  height: 6rem;
  display: flex;
  justify-content: center;
  margin: 0 auto 10rem;
  margin-top: 2rem;
`;

const Rule = styled.div`
  max-width: 720px;
  margin: 6rem auto;
  margin-top: 0;
  display: block;
`;

const Description = styled.div`
  padding: 2rem;
  font-size: 1.6rem;

  ul {
    margin-left: 2rem;
  }

  li {
    margin-bottom: 1.4rem;
  }

  b {
    border-bottom: 1px solid black;
  }

  h2 {
    font-size: 2.8rem;
    margin-bottom: 1rem;
  }
`;

export default ResultFinal;
